import dot from 'dot-prop-immutable';

import * as quoteItemTypes from 'modules/quotes/quoteItems/data/types';

import { denormalize, denormalizeEntity } from 'store/entities/selectors';
import * as schema from 'store/quotes/schema';

import { types } from './actions';

const initialState = {
  componentTableState: {},
  expandedComponentId: null,
  forceScrollToComponentRow: null,
  isAttachingByPartId: {},
  isCreatingEditableContour: false,
  isCreatingVectorization: false,
  isDeletingComponents: false,
  isFetchingById: {},
  hasFailedExpansionById: {},
  isSwitchingPrimaryFileById: {},
  lastSelectedComponent: null,
  selectedComponents: {},
  snapToComponentHeaderRequest: null,
  isManageRealTimePricingModalOpen: false,
};

const reducer = (state = initialState, action) => {
  const { payload } = action;
  switch (action.type) {
    case types.ACKNOWLEDGE_FORCE_SCROLL_TO_COMPONENT_ROW:
      return {
        ...state,
        forceScrollToComponentRow: null,
      };
    case types.EXPAND_COMPONENT_REQUEST:
      return dot.set(state, `isFetchingById.${payload.componentId}`, true);
    case types.EXPAND_COMPONENT_CLEAR:
      return {
        ...state,
        expandedComponentId: null,
      };
    case types.EXPAND_COMPONENT_SUCCESS: {
      const nextState = dot.delete(
        state,
        `isFetchingById.${payload.componentId}`,
      );
      return {
        ...nextState,
        expandedComponentId: payload.componentId,
        lastSelectedComponent: null,
        selectedComponents: {},
      };
    }
    case types.EXPAND_COMPONENT_ERROR: {
      const nextState = dot.delete(
        state,
        `isFetchingById.${payload.componentId}`,
      );
      return dot.set(
        nextState,
        `hasFailedExpansionById.${payload.componentId}`,
        true,
      );
    }
    case quoteItemTypes.EXPAND_QUOTE_ITEM_REQUEST:
    case quoteItemTypes.COLLAPSE_QUOTE_ITEM:
      return {
        ...state,
        expandedComponentId: null,
        lastSelectedComponent: null,
        selectedComponents: {},
      };
    case types.COLLAPSE_COMPONENT:
      return {
        ...state,
        expandedComponentId: null,
        forceScrollToComponentRow: state.expandedComponentId,
        lastSelectedComponent: null,
        selectedComponents: {},
      };
    case types.ATTACH_FILES_REQUEST:
      return dot.set(state, `isAttachingByPartId.${payload.partId}`, true);
    case types.ATTACH_FILES_SUCCESS:
    case types.ATTACH_FILES_ERROR:
    case types.ATTACH_FILES_CANCEL:
      return dot.set(state, `isAttachingByPartId.${payload.partId}`, false);
    case types.SELECT_COMPONENT_SELECT:
      return {
        ...state,
        selectedComponents: {
          ...state.selectedComponents,
          [payload.component.id]: state.selectedComponents[payload.component.id]
            ? false
            : payload.component,
        },
        lastSelectedComponent: payload.component,
      };
    case types.SELECT_COMPONENT_DESELECT_ALL:
      return {
        ...state,
        lastSelectedComponent: null,
        selectedComponents: {},
      };
    case types.SELECT_COMPONENT_DESELECT_BULK: {
      const toMerge = {};
      for (const comp of payload.components) {
        toMerge[comp.id] = false;
      }
      return {
        ...state,
        selectedComponents: {
          ...state.selectedComponents,
          ...toMerge,
        },
        lastSelectedComponent: payload.lastSelection,
      };
    }
    case types.SELECT_COMPONENT_MERGE_SELECTION: {
      const toMerge = {};
      for (const comp of payload.components) {
        toMerge[comp.id] = comp;
      }
      return {
        ...state,
        selectedComponents: {
          ...state.selectedComponents,
          ...toMerge,
        },
        lastSelectedComponent: payload.lastSelection,
      };
    }
    case types.COMPONENT_TABLE_STATE_MERGE:
      return {
        ...state,
        componentTableState: dot.merge(
          state.componentTableState,
          payload.parentComponentId,
          payload.state,
        ),
      };
    case types.DELETE_COMPONENTS_REQUEST:
      return {
        ...state,
        isDeletingComponents: true,
      };
    case types.DELETE_COMPONENTS_SUCCESS:
      return {
        ...state,
        isDeletingComponents: false,
        lastSelectedComponent: null,
        selectedComponents: {},
      };
    case types.DELETE_COMPONENTS_ERROR:
      return {
        ...state,
        isDeletingComponents: false,
      };
    case types.SNAP_TO_COMPONENT_HEADER_REQUEST:
      return {
        ...state,
        snapToComponentHeaderRequest: Math.random().toString(36).substring(7),
      };
    case types.EDIT_CONTOUR_REQUEST:
      return {
        ...state,
        isCreatingEditableContour: true,
      };
    case types.EDIT_CONTOUR_SUCCESS:
    case types.EDIT_CONTOUR_ERROR:
      return {
        ...state,
        isCreatingEditableContour: false,
      };
    case types.PAGE_VECTORIZATION_REQUEST:
      return {
        ...state,
        isCreatingVectorization: true,
      };
    case types.PAGE_VECTORIZATION_SUCCESS:
    case types.PAGE_VECTORIZATION_ERROR:
      return {
        ...state,
        isCreatingVectorization: false,
      };
    case types.SWITCH_PRIMARY_FILE_REQUEST:
      return dot.set(
        state,
        `isSwitchingPrimaryFileById.${payload.componentId}`,
        true,
      );
    case types.SWITCH_PRIMARY_FILE_SUCCESS:
    case types.SWITCH_PRIMARY_FILE_ERROR:
      return dot.set(
        state,
        `isSwitchingPrimaryFileById.${payload.componentId}`,
        false,
      );
    case types.OPEN_MANAGE_REAL_TIME_PRICING_MODAL:
      return {
        ...state,
        isManageRealTimePricingModalOpen: true,
      };
    case types.CLOSE_MANAGE_REAL_TIME_PRICING_MODAL:
      return {
        ...state,
        isManageRealTimePricingModalOpen: false,
      };
    default:
      return state;
  }
};

const getScope = (state) => state.modules.quotes.quoteItemComponents.root;

export const getIsManageRealTimePricingModalOpen = (state) =>
  getScope(state).isManageRealTimePricingModalOpen;

export const getExpandedComponentId = (state) =>
  getScope(state).expandedComponentId;

export const getExpandedComponent = (state) =>
  denormalize(state, getExpandedComponentId(state), schema.quoteItemComponent);

export const getForceScrollToComponentRow = (state) =>
  getScope(state).forceScrollToComponentRow;

export const getIsComponentFetchingById = (state, componentId) =>
  getScope(state).isFetchingById[componentId];

export const getHasComponentFailedToFetchById = (state, componentId) =>
  getScope(state).hasFailedExpansionById[componentId];

export const getBriefComponentById = (state, componentId) =>
  denormalizeEntity(
    state,
    'quoteItemBriefComponents',
    componentId,
    schema.quoteItemBriefComponent,
  );

export const getComponentById = (state, componentId) =>
  denormalizeEntity(
    state,
    'quoteItemComponents',
    componentId,
    schema.quoteItemComponent,
  );

const getParentComponentIds = (state, componentId, showFlatBOM) => {
  if (showFlatBOM) {
    const r = getBriefComponentById(state, componentId)?.parentComponentIds;
    if (r) {
      return r;
    }
  }
  return undefined;
};

/**
 @remarks: When lookupByAllParents is true, then we are looking at the flat BOM, and we need to consider very warning for component and therefore every parentComponentId
 */
export const getInsertionWarningMessages = (
  state,
  componentId,
  _parentComponentId,
  showFlatBOM,
) => {
  const parentComponentIds = getParentComponentIds(
    state,
    componentId,
    showFlatBOM,
  ) ?? [_parentComponentId];

  //Find first quoteItemInsertionWarnings
  //Per Luke: quoteItemInsertionWarnings is quote item level data. It is the same for every component in the entire quote item. So only need to find first case.
  let quoteItemInsertionWarnings;
  for (const parentComponentId of parentComponentIds) {
    const parentComponent = getComponentById(state, parentComponentId);
    if (parentComponent?.quoteItemInsertionWarnings) {
      quoteItemInsertionWarnings = parentComponent.quoteItemInsertionWarnings;
      break;
    }
  }

  //Warnings is a Set is used, because when `showFlatBOM === true`, there can be cases where the same match occurs multiple times
  const warnings = new Set();
  for (const {
    parentComponentId,
    childComponentId,
    sheetThicknessWarningMessage,
    holeSizeWarningMessage,
    holePositionWarningMessage,
    materialHardnessWarningMessage,
  } of quoteItemInsertionWarnings ?? []) {
    if (
      (showFlatBOM || parentComponentId === _parentComponentId) &&
      childComponentId === componentId
    ) {
      if (sheetThicknessWarningMessage) {
        warnings.add(sheetThicknessWarningMessage);
      }
      if (holeSizeWarningMessage) {
        warnings.add(holeSizeWarningMessage);
      }
      if (holePositionWarningMessage) {
        warnings.add(holePositionWarningMessage);
      }
      if (materialHardnessWarningMessage) {
        warnings.add(materialHardnessWarningMessage);
      }
    }
  }
  //When there are multiple warnings, they are separated by new line
  return warnings.size ? Array.from(warnings) : undefined;
};

export const getBriefComponentsById = (state, componentIds) =>
  denormalize(state, componentIds, [schema.quoteItemBriefComponent]);

export const getIsAttaching = (state, partId) =>
  getScope(state).isAttachingByPartId[partId];

export const getIsAttachingByPartId = (state) =>
  getScope(state).isAttachingByPartId;

export const getAreAnyFilesAttaching = (state) => {
  const attachingInfo = getScope(state).isAttachingByPartId;
  for (const key in attachingInfo) {
    if (attachingInfo[key]) {
      return true;
    }
  }
  return false;
};

export const getSelectedComponents = (state) =>
  getScope(state).selectedComponents;

export const getLastSelectedComponent = (state) =>
  getScope(state).lastSelectedComponent;

export const getIsComponentSelectedById = (state, componentId) =>
  !!getScope(state).selectedComponents[componentId];

export const getSelectedComponentsArray = (state) => {
  const selectedComponentsObject = getSelectedComponents(state);

  const result = [];
  for (const id in selectedComponentsObject) {
    if (selectedComponentsObject[id]) {
      result.push(getBriefComponentById(state, id));
    }
  }

  return result;
};

export const getAreSelectedComponents = (state) =>
  getSelectedComponentsArray(state).length > 0;

export const getComponentTableStateByParentComponentId = (
  state,
  parentComponentId,
) => {
  const componentTableState = getScope(state).componentTableState;
  return componentTableState[parentComponentId] || {};
};

export const getIsDeletingComponents = (state) =>
  getScope(state).isDeletingComponents;

export const getSnapToComponentHeaderRequest = (state) =>
  getScope(state).snapToComponentHeaderRequest;

export const getIsCreatingVectorization = (state) =>
  getScope(state).isCreatingVectorization;

export const getIsCreatingEditableContour = (state) =>
  getScope(state).isCreatingEditableContour;

export const getIsSwitchingPrimaryFile = (state, componentId) =>
  getScope(state).isSwitchingPrimaryFileById[(componentId || '').toString()];

export default reducer;
